import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../store/auth-store';
import { useProjectStore } from '../store/project-store';
import { createGitApi } from '@shared/api';
function defaultCommitMessage() {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const time = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    return `Update project files [${date} ${time}]`;
}
async function reloadAllProjects() {
    const client = useAuthStore.getState().getClient();
    if (!client)
        return;
    const ps = useProjectStore.getState();
    const projects = ps.projects;
    for (const p of projects) {
        try {
            await ps.loadProjectContent(p.project_id, client);
        }
        catch {
            // skip projects that fail to load
        }
    }
}
async function saveAllProjects(message) {
    const client = useAuthStore.getState().getClient();
    if (!client)
        return;
    const ps = useProjectStore.getState();
    for (const p of ps.projects) {
        try {
            await ps.saveProjectContent(p.project_id, client, message);
        }
        catch {
            // skip projects that fail to save
        }
    }
}
export function SyncIndicator() {
    const getClient = useAuthStore((s) => s.getClient);
    const serverUrl = useAuthStore((s) => s.serverUrl);
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const [state, setState] = useState('loading');
    const [diff, setDiff] = useState(null);
    const [open, setOpen] = useState(false);
    const [progressMsg, setProgressMsg] = useState('');
    const [commitMessage, setCommitMessage] = useState(defaultCommitMessage());
    const [showCommitInput, setShowCommitInput] = useState(false);
    const [conflictFiles, setConflictFiles] = useState([]);
    const [resolved, setResolved] = useState(new Set());
    const dropdownRef = useRef(null);
    const api = () => {
        const client = getClient();
        return client ? createGitApi(client) : null;
    };
    const checkStatus = async () => {
        const g = api();
        if (!g)
            return false;
        try {
            const s = await g.status();
            return s.connected;
        }
        catch {
            return false;
        }
    };
    const checkDiff = async () => {
        const g = api();
        if (!g) {
            setState('server-offline');
            return;
        }
        const connected = await checkStatus();
        if (!connected) {
            setState('disconnected');
            return;
        }
        try {
            const d = await g.diff();
            setDiff(d);
            const changed = d.changed || [];
            const untracked = d.untracked || [];
            const conflicts = d.conflicts || [];
            if (conflicts.length > 0) {
                setState('conflict');
                setConflictFiles(conflicts);
            }
            else if (changed.length > 0 || untracked.length > 0 || d.ahead > 0) {
                setState('dirty');
            }
            else {
                setState('clean');
            }
        }
        catch {
            setState('disconnected');
        }
    };
    useEffect(() => {
        if (!serverUrl) {
            setState('server-offline');
            return;
        }
        if (!isAuthenticated) {
            setState('disconnected');
            return;
        }
        checkDiff();
        const interval = setInterval(checkDiff, 30000);
        return () => clearInterval(interval);
    }, [serverUrl, isAuthenticated]);
    useEffect(() => {
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);
    const handlePush = async (message) => {
        const g = api();
        if (!g)
            return;
        setState('pushing');
        setProgressMsg('Saving changes...');
        try {
            await saveAllProjects(message);
            setProgressMsg('Committing & pushing...');
            const res = await g.push(message);
            if (res.status === 'conflict') {
                setState('conflict');
                setConflictFiles(res.conflicts || []);
                setResolved(new Set());
                setProgressMsg('');
            }
            else if (res.status === 'pushed') {
                setState('clean');
                setDiff(null);
                setProgressMsg('');
                useProjectStore.getState().resetPendingChanges();
                const syncRes = await g.sync();
                const ps = useProjectStore.getState();
                ps.syncFromGit(syncRes.files);
                await reloadAllProjects();
                checkDiff();
            }
        }
        catch (err) {
            setProgressMsg(err?.message || 'Push failed');
            setTimeout(() => setProgressMsg(''), 3000);
            checkDiff();
        }
    };
    const handleResolve = async (file, choice) => {
        const g = api();
        if (!g)
            return;
        try {
            const res = await g.resolve(file, choice);
            setResolved((prev) => new Set(prev).add(file));
            if (res.status === 'pushed') {
                setState('clean');
                setDiff(null);
                setProgressMsg('');
                useProjectStore.getState().resetPendingChanges();
                const syncRes = await g.sync();
                const ps = useProjectStore.getState();
                ps.syncFromGit(syncRes.files);
                await reloadAllProjects();
                checkDiff();
            }
        }
        catch (err) {
            setProgressMsg(err?.message || 'Resolution failed');
        }
    };
    const handleAbort = async () => {
        const g = api();
        if (!g)
            return;
        try {
            await g.abort();
            setState('dirty');
            setConflictFiles([]);
            setOpen(false);
            checkDiff();
        }
        catch (err) {
            setProgressMsg(err?.message || 'Abort failed');
        }
    };
    const pendingChanges = useProjectStore((s) => s.pendingChanges);
    const changedCount = diff ? (diff.changed || []).length + (diff.untracked || []).length : 0;
    return (_jsxs("div", { className: "relative", ref: dropdownRef, children: [_jsxs("button", { onClick: () => {
                    if (state === 'conflict') {
                        setOpen(true);
                    }
                    else {
                        setOpen(!open);
                    }
                }, className: "p-2 rounded-lg hover:bg-bg-secondary transition-colors relative", title: state === 'clean' && pendingChanges === 0 ? 'Synced' :
                    state === 'clean' && pendingChanges > 0 ? `${pendingChanges} change(s) pending — push to sync` :
                        state === 'dirty' ? `${changedCount} file(s) changed${pendingChanges > 0 ? `, ${pendingChanges} local change(s)` : ''}` :
                            state === 'conflict' ? 'Merge conflict' :
                                state === 'pushing' ? 'Pushing...' :
                                    state === 'server-offline' ? 'No server configured' :
                                        state === 'disconnected' ? 'Not connected to Git' : '', children: [state === 'loading' && (_jsx("svg", { className: "w-5 h-5 text-text-muted animate-pulse", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" }) })), state === 'clean' && pendingChanges === 0 && (_jsxs("svg", { className: "w-5 h-5 text-green-500", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: [_jsx("circle", { cx: "12", cy: "12", r: "10", strokeWidth: "2" }), _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 13l4 4L19 7" })] })), state === 'clean' && pendingChanges > 0 && (_jsx("svg", { className: "w-5 h-5 text-yellow-500", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 6v6m0 0v6m0-6h6m-6 0H6" }) })), state === 'dirty' && (_jsx("div", { className: "relative", children: _jsx("svg", { className: "w-5 h-5 text-yellow-500", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 6v6m0 0v6m0-6h6m-6 0H6" }) }) })), state === 'conflict' && (_jsx("svg", { className: "w-5 h-5 text-red-500", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" }) })), state === 'pushing' && (_jsx("svg", { className: "w-5 h-5 text-blue-500 animate-spin", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" }) })), state === 'server-offline' && (_jsx("svg", { className: "w-5 h-5 text-gray-400", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 12h14M12 5l7 7-7 7" }) })), state === 'disconnected' && (_jsxs("svg", { className: "w-5 h-5 text-red-400", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: [_jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" }), _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M19 5l-14 14" })] })), pendingChanges > 0 && (_jsxs("span", { className: "absolute -top-1.5 -right-1.5 bg-primary text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none shadow-sm", children: ["+", pendingChanges > 9 ? '9' : pendingChanges] }))] }), open && state === 'server-offline' && (_jsxs("div", { className: "absolute right-0 mt-2 w-72 bg-bg-secondary border border-card-border rounded-lg shadow-lg z-50 p-3", children: [_jsx("p", { className: "text-sm font-medium text-text-primary mb-2", children: "No server configured" }), _jsx("p", { className: "text-xs text-text-muted mb-3", children: "Set up a server URL in Settings to enable sync." }), _jsx("button", { onClick: () => setOpen(false), className: "w-full py-1.5 px-3 bg-primary text-white text-sm rounded-lg hover:opacity-90", children: "Open Settings" })] })), open && state === 'disconnected' && (_jsxs("div", { className: "absolute right-0 mt-2 w-72 bg-bg-secondary border border-card-border rounded-lg shadow-lg z-50 p-3", children: [_jsx("p", { className: "text-sm font-medium text-text-primary mb-2", children: "Server offline" }), _jsx("p", { className: "text-xs text-text-muted mb-3", children: "Server is configured but not reachable. Check your server connection in Settings." }), _jsx("button", { onClick: () => setOpen(false), className: "w-full py-1.5 px-3 bg-primary text-white text-sm rounded-lg hover:opacity-90", children: "Open Settings" })] })), open && ((state === 'dirty' || state === 'pushing') || (state === 'clean' && pendingChanges > 0)) && (_jsxs("div", { className: "absolute right-0 mt-2 w-80 bg-bg-secondary border border-card-border rounded-lg shadow-lg z-50 p-3", children: [_jsx("p", { className: "text-sm font-medium text-text-primary mb-2", children: pendingChanges > 0 ? `${pendingChanges} local change(s) pending` : `${changedCount} file(s) changed` }), diff && ((diff.changed || []).length > 0 || (diff.untracked || []).length > 0) && (_jsx("ul", { className: "text-xs text-text-muted mb-3 max-h-32 overflow-y-auto space-y-1", children: [...(diff.changed || []), ...(diff.untracked || [])].map((f) => (_jsx("li", { className: "truncate", children: f }, f))) })), diff && (diff.ahead > 0 || diff.behind > 0) && (_jsxs("p", { className: "text-xs text-text-muted mb-2", children: [diff.ahead > 0 && `${diff.ahead} ahead `, diff.behind > 0 && `${diff.behind} behind`] })), showCommitInput ? (_jsxs(_Fragment, { children: [_jsx("textarea", { value: commitMessage, onChange: (e) => setCommitMessage(e.target.value), rows: 3, className: "input w-full text-sm mb-2", placeholder: "Commit message...", "aria-label": "Commit message", autoFocus: true }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { onClick: () => { setShowCommitInput(false); setCommitMessage(defaultCommitMessage()); }, className: "flex-1 py-1.5 px-3 text-sm text-text-muted border border-card-border rounded-lg hover:bg-bg-primary", children: "Cancel" }), _jsx("button", { onClick: () => handlePush(commitMessage), disabled: state === 'pushing' || !commitMessage.trim(), className: "flex-1 py-1.5 px-3 bg-primary text-white text-sm rounded-lg hover:opacity-90 disabled:opacity-50", children: state === 'pushing' ? 'Pushing...' : 'Commit & Push' })] })] })) : (_jsx("button", { onClick: () => setShowCommitInput(true), disabled: state === 'pushing', className: "w-full py-1.5 px-3 bg-primary text-white text-sm rounded-lg hover:opacity-90 disabled:opacity-50", children: state === 'pushing' ? 'Pushing...' : 'Push Changes' })), progressMsg && _jsx("p", { className: "text-xs text-red-500 mt-1", children: progressMsg })] })), open && state === 'conflict' && (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/40", onClick: (e) => { if (e.target === e.currentTarget)
                    setOpen(false); }, children: _jsxs("div", { className: "bg-bg-secondary border border-card-border rounded-lg shadow-xl w-full max-w-lg p-6 mx-4", onClick: (e) => e.stopPropagation(), children: [_jsx("h3", { className: "text-lg font-semibold text-text-primary mb-1", children: "Merge Conflict" }), _jsx("p", { className: "text-sm text-text-muted mb-4", children: "Conflicting files need to be resolved before pushing." }), _jsx("div", { className: "space-y-3 max-h-60 overflow-y-auto mb-4", children: conflictFiles.map((file) => (_jsxs("div", { className: "flex items-center justify-between p-3 bg-bg-primary rounded-lg", children: [_jsx("span", { className: `text-sm ${resolved.has(file) ? 'text-green-500 line-through' : 'text-text-primary'}`, children: file }), !resolved.has(file) && (_jsxs("div", { className: "flex gap-2", children: [_jsx("button", { onClick: () => handleResolve(file, 'ours'), className: "px-3 py-1 text-xs bg-bg-secondary border border-card-border rounded-lg hover:bg-primary hover:text-white transition-colors", children: "Keep mine" }), _jsx("button", { onClick: () => handleResolve(file, 'theirs'), className: "px-3 py-1 text-xs bg-bg-secondary border border-card-border rounded-lg hover:bg-primary hover:text-white transition-colors", children: "Keep theirs" })] })), resolved.has(file) && (_jsx("span", { className: "text-xs text-green-500", children: "Resolved" }))] }, file))) }), _jsxs("div", { className: "flex justify-between", children: [_jsx("button", { onClick: handleAbort, className: "px-4 py-2 text-sm text-text-muted hover:text-red-500 transition-colors", children: "Abort" }), conflictFiles.length > 0 && conflictFiles.every((f) => resolved.has(f)) && (_jsx("button", { onClick: () => handlePush(commitMessage), className: "px-4 py-2 text-sm bg-primary text-white rounded-lg hover:opacity-90", children: "Continue Push" }))] }), progressMsg && _jsx("p", { className: "text-xs text-red-500 mt-2", children: progressMsg })] }) }))] }));
}
//# sourceMappingURL=SyncIndicator.js.map